/**
 * staticDataCache
 * ───────────────
 * Tiny SWR (stale-while-revalidate) layer for static-ish endpoints
 * like providers, locations, pos-codes, organizations, users, and
 * appointment statuses.
 *
 * Why?
 *   On every dashboard mount the app re-fetches these arrays.  They
 *   change on the order of once-a-week (when an admin adds a new
 *   provider/location/etc).  Re-downloading them on every page
 *   reload costs precious LCP seconds, especially on cold container
 *   restarts.
 *
 * How?
 *   1. Read the cached payload from sessionStorage synchronously
 *      and return it immediately (the "stale" half).
 *   2. Fire the network request in the background and replace the
 *      cache + the consumer's state when the fresh response lands
 *      (the "revalidate" half).
 *
 * Storage: sessionStorage, NOT localStorage.  Reasons:
 *   • The user's auth context lives in sessionStorage already, so
 *     it dies on tab close — which is what we want for these
 *     payloads (they may contain mildly sensitive PHI-adjacent data
 *     like provider names + emails, which we don't want persisting
 *     to disk across sessions).
 *   • Each tab gets an independent cache, so impersonation in one
 *     tab can't poison another tab.
 *
 * TTL: defaults to 5 minutes for foreground reuse, with an absolute
 * 24h hard expiry to prevent edge cases where stale staff lists
 * outlive a server-side change.  Both knobs are configurable per
 * call.
 *
 * Cache invalidation:
 *   • Called from AuthContext on logout via `clearStaticDataCache()`.
 *   • Callers that perform mutations (admin adds a provider) should
 *     call `bustStaticDataKey('providers')` after the save resolves.
 */

const CACHE_PREFIX = 'sdc:v1:';  // bump version to invalidate all keys at once
const DEFAULT_FRESH_TTL_MS = 5 * 60 * 1000;     // 5 min
const HARD_EXPIRY_MS = 24 * 60 * 60 * 1000;     // 24 h

function safeRead(key) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.t !== 'number') return null;
    if (Date.now() - parsed.t > HARD_EXPIRY_MS) {
      sessionStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function safeWrite(key, data) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
      t: Date.now(),
      d: data,
    }));
  } catch {
    // Quota exceeded or storage disabled — silently skip.
  }
}

/**
 * `swrFetch(key, fetcher, { setter, freshTtlMs })`
 *
 *   key       — string identifier for the cache slot, e.g. 'providers'.
 *   fetcher   — async () => responseData (caller-supplied; usually
 *               wraps `axios.get(...).then(r => r.data)` ).
 *   setter    — optional React setState callback.  When provided,
 *               this function will call it twice on a cache hit:
 *               once immediately with the cached value, then once
 *               again with the fresh value when the revalidate
 *               completes.
 *   freshTtlMs — soft TTL.  If the cache entry is younger than this,
 *               no network revalidate happens.  Defaults to 5 min.
 *
 * Returns: a Promise that resolves to the data (fresh OR cached
 * if revalidate fails).
 */
export async function swrFetch(key, fetcher, opts = {}) {
  const { setter, freshTtlMs = DEFAULT_FRESH_TTL_MS } = opts;
  const cached = safeRead(key);
  const cacheIsFresh = cached && (Date.now() - cached.t < freshTtlMs);

  // STALE path — emit cached value immediately to the consumer.
  if (cached && setter) {
    try { setter(cached.d); } catch { /* consumer may have unmounted */ }
  }

  // If the cache is fresh, skip the network round-trip.
  if (cacheIsFresh) {
    return cached.d;
  }

  // REVALIDATE — go to the wire.
  try {
    const fresh = await fetcher();
    safeWrite(key, fresh);
    if (setter) {
      try { setter(fresh); } catch { /* consumer may have unmounted */ }
    }
    return fresh;
  } catch (err) {
    // Network failed — if we have a stale cache, return that instead
    // of bubbling the error.  Better stale data than no data.
    if (cached) return cached.d;
    throw err;
  }
}

/** Synchronously read the cached value without firing a network call. */
export function readStaticData(key) {
  const cached = safeRead(key);
  return cached ? cached.d : null;
}

/** Manually update a cache entry — call after admin mutations. */
export function bustStaticDataKey(key) {
  try {
    sessionStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // ignore
  }
}

/** Clear EVERY static-data cache entry.  Called on logout. */
export function clearStaticDataCache() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

const _exports = {
  swrFetch,
  readStaticData,
  bustStaticDataKey,
  clearStaticDataCache,
};
export default _exports;
